/**
 * Parses the result body of a long-task tool (ADR 059). A conforming MCP server
 * shapes every submit/status/fetch result into a fixed envelope server-side, so
 * the UI does no JSONPath and carries no per-server parsing config: one parser,
 * keyed by the tool's declared `role`, shape-checks the corresponding envelope.
 *
 * `ToolMessage.content` is polymorphic across the chain pipeline: a raw JSON
 * string, a list of `{type:"text"}` content blocks, or a `json.dumps`'d object.
 * `extractEnvelopeObject` tolerates all three.
 *
 * Each parser returns `null` when the body does not parse or does not satisfy
 * the role's shape; callers leave the registry untouched and the normal
 * tool-result chrome still renders the raw payload.
 */
import type { LongTaskRole } from "types/longTask";
import type { ResultLink } from "types/TaskRegistry";
import { isTextBlock } from "utilities/toolResultContent";

export interface SubmitEnvelope {
  job_id: string;
  correlation_id?: string;
  display_name?: string;
}

interface StatusEnvelope {
  status: string;
  reason: string | null;
  raw_status: string;
}

interface FetchEnvelope {
  links: ResultLink[];
  archive_url?: string;
  terminal_reason?: string;
  is_terminal?: boolean;
}

type LongTaskEnvelope<R extends LongTaskRole> = R extends "submit"
  ? SubmitEnvelope
  : R extends "status"
  ? StatusEnvelope
  : FetchEnvelope;

/**
 * Normalizes a polymorphic `ToolMessage.content` value to a parsed object, or
 * `null`. An array concatenates the `.text` of every `type:"text"` block and
 * recurses; a string is `JSON.parse`'d; anything else is rejected.
 */
function extractEnvelopeObject(
  rawContent: unknown
): Record<string, unknown> | null {
  if (Array.isArray(rawContent)) {
    const text = rawContent
      .filter(isTextBlock)
      .map((block) => block.text)
      .join("");
    if (!text) return null;
    return extractEnvelopeObject(text);
  }
  if (typeof rawContent !== "string" || !rawContent) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawContent);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  return parsed as Record<string, unknown>;
}

function parseSubmit(obj: Record<string, unknown>): SubmitEnvelope | null {
  const jobId = obj.job_id;
  if (typeof jobId !== "string" || jobId.length === 0) return null;
  const correlationId =
    typeof obj.correlation_id === "string" && obj.correlation_id.length > 0
      ? obj.correlation_id
      : undefined;
  // Trimmed here, at the parse boundary, so a whitespace-only label arrives as
  // absent rather than as a string that renders blank on the card.
  const displayName =
    typeof obj.display_name === "string" && obj.display_name.trim().length > 0
      ? obj.display_name.trim()
      : undefined;
  return {
    job_id: jobId,
    correlation_id: correlationId,
    display_name: displayName,
  };
}

function parseStatus(obj: Record<string, unknown>): StatusEnvelope | null {
  const status = obj.status;
  if (typeof status !== "string" || status.length === 0) return null;
  const reason = typeof obj.reason === "string" ? obj.reason : null;
  const rawStatus =
    typeof obj.raw_status === "string" ? obj.raw_status : status;
  return { status, reason, raw_status: rawStatus };
}

/**
 * True when `value` is an absolute http(s) URL. Fetch result links and archive
 * URLs are untrusted MCP/LLM output rendered as anchor `href`s (ADR 059), so a
 * `javascript:` / `data:` scheme would execute in the app origin on click.
 * React does not filter `href` schemes, so we reject anything but http(s) here,
 * at the parse boundary, before the value can reach the persisted row.
 */
export function isSafeHttpUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function parseFetch(obj: Record<string, unknown>): FetchEnvelope | null {
  // A fetch body is a results envelope only when it carries a links array; a
  // not-ready / terminal-failure body carries is_terminal but no links. Accept
  // either, but require at least one of the two discriminating shapes.
  const hasLinks = Array.isArray(obj.links);
  const hasTerminalFlag = typeof obj.is_terminal === "boolean";
  if (!hasLinks && !hasTerminalFlag) return null;
  const links = hasLinks
    ? (obj.links as unknown[])
        .filter(
          (l): l is Record<string, unknown> =>
            typeof l === "object" && l !== null
        )
        .filter((l): l is Record<string, unknown> & { permalink: string } =>
          isSafeHttpUrl(l.permalink)
        )
        .map((l) => ({
          label: typeof l.label === "string" ? l.label : "Result",
          permalink: l.permalink,
        }))
    : [];
  const archiveUrl = isSafeHttpUrl(obj.archive_url)
    ? obj.archive_url
    : undefined;
  const terminalReason =
    typeof obj.terminal_reason === "string" ? obj.terminal_reason : undefined;
  const isTerminal =
    typeof obj.is_terminal === "boolean" ? obj.is_terminal : undefined;
  return {
    links,
    archive_url: archiveUrl,
    terminal_reason: terminalReason,
    is_terminal: isTerminal,
  };
}

export function parseLongTaskResult<R extends LongTaskRole>(
  rawContent: unknown,
  role: R
): LongTaskEnvelope<R> | null {
  const obj = extractEnvelopeObject(rawContent);
  if (!obj) return null;
  switch (role) {
    case "submit":
      return parseSubmit(obj) as LongTaskEnvelope<R> | null;
    case "status":
      return parseStatus(obj) as LongTaskEnvelope<R> | null;
    case "fetch":
      return parseFetch(obj) as LongTaskEnvelope<R> | null;
    default:
      return null;
  }
}

/**
 * True when a fetch carried something to present: per-report links or an archive
 * URL. An archive-only success counts — it is still a result to restore.
 *
 * Takes the two values rather than a whole envelope or row, because both shapes
 * ask this question under different field names: the fetch envelope's
 * `links`/`archive_url` when the tracker's poll reads a body, and the task row's
 * `resultLinks`/`archiveUrl` when the card decides whether to offer another fetch.
 * One predicate keeps them from disagreeing, which matters because "archive but no
 * links is still a result" is the sort of rule that gets quietly re-derived wrongly.
 */
export function hasResultPayoff(
  links: ResultLink[] | null | undefined,
  archiveUrl: string | undefined
): boolean {
  return (links?.length ?? 0) > 0 || !!archiveUrl;
}

/** How much of a `job_id` stands in for a missing label. */
const JOB_ID_LABEL_LENGTH = 12;

/**
 * Picks the display name shown on a long-task card: the submit envelope's
 * `display_name`, else a shortened `job_id`. The server owns the label, so the UI
 * reads no tool-input field to build one.
 *
 * A plain `??` suffices because `parseSubmit` has already done the normalizing:
 * anything not a usable string — absent, non-string, empty, or whitespace-only —
 * reaches here as `undefined`.
 */
export function pickLongTaskDisplayName(envelope: SubmitEnvelope): string {
  const { display_name: displayName, job_id: jobId } = envelope;
  if (displayName) return displayName;
  return jobId.length > JOB_ID_LABEL_LENGTH
    ? `${jobId.slice(0, JOB_ID_LABEL_LENGTH)}…`
    : jobId;
}
